/**
 * Agent Color Identities
 *
 * Each agent has a unique color matching its identity on the landing page.
 * Director=purple, Character=yellow, Location=green, Storyboard=green,
 * Video=orange, Sound=cyan, ScriptWriter=indigo, Object=amber.
 *
 * COLOR STATES:
 * - Idle:     agent color at 15-18% opacity (agent present, not speaking)
 * - Hover:    agent color at 45% opacity (agent available)
 * - Active:   agent color at 100% opacity (agent currently working)
 * - Selected: agent color at 100% + box-shadow glow at 15-20% opacity
 *
 * CRITICAL RULES:
 * - Glow on active/selected is the ONLY decorative effect permitted
 * - NO gradients on nodes
 * - NO left-border color strips on chat bubbles or node cards
 * - NO color fills on idle nodes
 */

export type AgentType =
  | 'director'
  | 'character'
  | 'object'
  | 'location'
  | 'storyboard'
  | 'scriptwriter'
  | 'video'
  | 'sound'

export interface AgentColorConfig {
  /** Primary hex color (100% opacity) */
  hex: string
  /** Dim variant for dark backgrounds */
  dim: string
  /** Text color when on active background */
  text: string
  /** Idle state - 15-18% opacity */
  idle: string
  /** Hover state - 45% opacity */
  hover: string
  /** Focus state - 70% opacity (between hover and active) */
  focus: string
  /** Active state - 100% opacity */
  active: string
  /** Box-shadow glow for selected state */
  glow: string
  /** Idle opacity percentage (for reference) */
  idleOpacity: number
}

export const AGENT_COLORS: Record<AgentType, AgentColorConfig> = {
  director: {
    hex: '#8B5CF6',
    dim: '#3B1A8C',
    text: '#E8DAFE',
    idle: 'rgba(139,92,246,0.18)',
    hover: 'rgba(139,92,246,0.45)',
    focus: 'rgba(139,92,246,0.70)',
    active: '#8B5CF6',
    glow: '0 0 20px 5px rgba(139,92,246,0.20)',
    idleOpacity: 18,
  },
  character: {
    hex: '#EAB308',
    dim: '#713F12',
    text: '#FEF9C3',
    idle: 'rgba(234,179,8,0.15)',
    hover: 'rgba(234,179,8,0.45)',
    focus: 'rgba(234,179,8,0.70)',
    active: '#EAB308',
    glow: '0 0 18px 4px rgba(234,179,8,0.18)',
    idleOpacity: 15,
  },
  object: {
    hex: '#F59E0B',
    dim: '#78350F',
    text: '#FEF3C7',
    idle: 'rgba(245,158,11,0.15)',
    hover: 'rgba(245,158,11,0.45)',
    focus: 'rgba(245,158,11,0.70)',
    active: '#F59E0B',
    glow: '0 0 16px 4px rgba(245,158,11,0.16)',
    idleOpacity: 15,
  },
  location: {
    hex: '#22C55E',
    dim: '#14532D',
    text: '#DCFCE7',
    idle: 'rgba(34,197,94,0.18)',
    hover: 'rgba(34,197,94,0.45)',
    focus: 'rgba(34,197,94,0.70)',
    active: '#22C55E',
    glow: '0 0 20px 5px rgba(34,197,94,0.20)',
    idleOpacity: 18,
  },
  storyboard: {
    hex: '#22C55E',
    dim: '#14532D',
    text: '#DCFCE7',
    idle: 'rgba(34,197,94,0.15)',
    hover: 'rgba(34,197,94,0.45)',
    focus: 'rgba(34,197,94,0.70)',
    active: '#22C55E',
    glow: '0 0 20px 5px rgba(34,197,94,0.20)',
    idleOpacity: 15,
  },
  scriptwriter: {
    hex: '#6366F1',
    dim: '#312E81',
    text: '#E0E7FF',
    idle: 'rgba(99,102,241,0.15)',
    hover: 'rgba(99,102,241,0.45)',
    focus: 'rgba(99,102,241,0.70)',
    active: '#6366F1',
    glow: '0 0 16px 4px rgba(99,102,241,0.16)',
    idleOpacity: 15,
  },
  video: {
    hex: '#F97316',
    dim: '#7C2D12',
    text: '#FFEDD5',
    idle: 'rgba(249,115,22,0.18)',
    hover: 'rgba(249,115,22,0.45)',
    focus: 'rgba(249,115,22,0.70)',
    active: '#F97316',
    glow: '0 0 22px 6px rgba(249,115,22,0.22)',
    idleOpacity: 18,
  },
  sound: {
    hex: '#06B6D4',
    dim: '#164E63',
    text: '#CFFAFE',
    idle: 'rgba(6,182,212,0.15)',
    hover: 'rgba(6,182,212,0.45)',
    focus: 'rgba(6,182,212,0.70)',
    active: '#06B6D4',
    glow: '0 0 18px 4px rgba(6,182,212,0.18)',
    idleOpacity: 15,
  },
}

/**
 * Get agent color config by type
 */
export function getAgentColor(agentType: AgentType): AgentColorConfig {
  return AGENT_COLORS[agentType]
}

/**
 * Get border color based on agent type and state
 */
export function getAgentBorderColor(
  agentType: AgentType | undefined,
  state: 'idle' | 'hover' | 'active' | 'selected'
): string {
  if (!agentType) return 'transparent'
  const config = AGENT_COLORS[agentType]
  
  switch (state) {
    case 'idle':
      return config.idle
    case 'hover':
      return config.hover
    case 'active':
    case 'selected':
      return config.active
    default:
      return config.idle
  }
}
